import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";

const socket = io("https://warfc.com", {
  path: "/socket.io",
  transports: ["websocket"],
});

export default function DisplayPage() {
  const params = new URLSearchParams(window.location.search);
  const ring = (params.get("ring") || "A").toUpperCase();

  const [currentFight, setCurrentFight] = useState(null);
  const [nextFights, setNextFights] = useState([]);
  const [timer, setTimer] = useState(0);
  const [phase, setPhase] = useState("round");

  const warningPlayed = useRef(false);
  const endPlayed = useRef(false);

  const warningSound = useRef(new Audio("/sounds/warning.mp3"));
  const endSound = useRef(new Audio("/sounds/end.mp3"));

  /* ---------------- SOCKET CONNECTION ---------------- */
  useEffect(() => {
    socket.emit("join_ring", ring);

    const updateState = (data) => {
      const ringData = data[ring];
      if (!ringData) return;

      const fight = ringData.currentFight || null;
      const clonedFight = fight
        ? {
            ...fight,
            rounds: (fight.rounds || []).map((r) => ({
              ...r,
              votes: { ...r.votes },
            })),
          }
        : null;

      setCurrentFight(clonedFight);
      setTimer(ringData.timer ?? clonedFight?.roundDuration ?? 0);
      setPhase(ringData.phase ?? "round");

      const allFights = ringData.upcomingFights || [];
      const currentIndex = allFights.findIndex((f) => f.id === fight?.id);
      setNextFights(currentIndex >= 0 ? allFights.slice(currentIndex + 1) : allFights);
    };

    const updateTimer = ({ timer: t, phase: p, ring: r }) => {
      if (r !== ring) return;
      setTimer(t);
      setPhase(p);
    };

    socket.on("judging_state", updateState);
    socket.on("timer_update", updateTimer);

    return () => {
      socket.off("judging_state", updateState);
      socket.off("timer_update", updateTimer);
    };
  }, [ring]);

  /* ---------------- SOUNDS ---------------- */
  useEffect(() => {
    if (!currentFight) return;

    if (timer === 10 && !warningPlayed.current) {
      warningPlayed.current = true;
      warningSound.current.currentTime = 0;
      warningSound.current.play().catch(() => {});
    }

    if (timer === 0 && !endPlayed.current) {
      endPlayed.current = true;
      endSound.current.currentTime = 0;
      endSound.current.play().catch(() => {});
    }
  }, [timer, currentFight]);

  useEffect(() => {
    warningPlayed.current = false;
    endPlayed.current = false;
  }, [currentFight?.round]);

  /* ---------------- TIMER PROGRESS ---------------- */
  const totalTime =
    phase === "rest"
      ? currentFight?.restDuration ?? 30
      : currentFight?.roundDuration ?? 60;

  const progress = totalTime
    ? Math.max(0, Math.min((timer / totalTime) * 100, 100))
    : 0;

  /* ---------------- SCORE CALC ---------------- */
  const rounds =
    currentFight?.rounds && currentFight.rounds.length > 0
      ? currentFight.rounds
      : [{ votes: {} }, { votes: {} }, { votes: {} }];

  const totalRed = rounds.reduce((sum, r) => {
    const votes = Object.values(r.votes || {});
    return sum + votes.reduce((s, v) => s + v.red, 0);
  }, 0);

  const totalBlue = rounds.reduce((sum, r) => {
    const votes = Object.values(r.votes || {});
    return sum + votes.reduce((s, v) => s + v.blue, 0);
  }, 0);

  /* ---------------- RENDER ROUND CELL ---------------- */
  const renderRoundCell = (round, side) => {
    const base =
      "flex flex-col items-center justify-center w-full h-full text-2xl font-extrabold";

    if (!round) return <div className={base}>–</div>;

    const votes = Object.values(round.votes || {});
    const score = votes.reduce((sum, v) => sum + (side === "red" ? v.red : v.blue), 0);

    let liveWinner = null;
    if (round.locked && votes.length > 0) {
      const totalRed = votes.reduce((s, v) => s + v.red, 0);
      const totalBlue = votes.reduce((s, v) => s + v.blue, 0);
      if (totalRed > totalBlue) liveWinner = "red";
      else if (totalBlue > totalRed) liveWinner = "blue";
    }

    const isWin = side === liveWinner;
    const pulseClass = round.locked && isWin ? "bg-yellow-400 text-black animate-pulse" : "";

    const dots = votes.map((v, j) => {
      const val = side === "red" ? v.red : v.blue;
      const color = val === 10 ? (side === "red" ? "bg-red-500" : "bg-blue-500") : "bg-gray-500";
      return <span key={j} className={`w-3 h-3 rounded-full ${color} mx-0.5`} />;
    });

    return (
      <div className={`${base} ${pulseClass} ${!pulseClass ? "bg-gray-800" : ""}`}>
        <div>{score || "–"}</div>
        {dots.length > 0 && <div className="flex mt-1">{dots}</div>}
      </div>
    );
  };

  /* ---------------- RENDER ---------------- */
  return (
  <div className="w-screen h-screen bg-black text-white flex flex-col items-center justify-center p-8 relative overflow-hidden">


        {/* ---------------- BACKGROUND ---------------- */}
    <div className="absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] rounded-full bg-yellow-500 opacity-50 animate-pulse-slower blur-3xl"></div>
      <div className="absolute top-1/2 left-3/4 w-[300px] h-[300px] rounded-full bg-red-500 opacity-40 animate-pulse-slow blur-2xl"></div>
      <div className="absolute bottom-1/4 left-1/2 w-[200px] h-[200px] rounded-full bg-blue-500 opacity-30 animate-pulse-slower blur-xl"></div>
    </div>
      {/* TIMER CIRCLE */}
      <div className="relative w-72 h-72 mb-4">
        <svg className="absolute inset-0" viewBox="0 0 300 300">
          <circle cx="150" cy="150" r="120" stroke="gray" strokeWidth="12" fill="none" />
          <circle
            cx="150"
            cy="150"
            r="120"
            stroke={timer <= 10 ? "red" : "yellow"}
            strokeWidth="12"
            fill="none"
            strokeDasharray={2 * Math.PI * 120}
            strokeDashoffset={(1 - progress / 100) * 2 * Math.PI * 120}
            strokeLinecap="round"
            transform="rotate(-90 150 150)"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-8xl font-extrabold text-yellow-400">
            {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, "0")}
          </span>
        </div>
      </div>

      {!currentFight ? (
        <div className="text-5xl font-extrabold text-gray-600 animate-pulse mb-8">
          Waiting for next fight
        </div>
      ) : (
        <>
          {/* HEADER */}
          <div className="flex items-center justify-between w-full max-w-4xl mb-6 px-4">
            <div className="w-1/3 flex items-center justify-center">
              <span className="text-2xl md:text-3xl font-bold text-yellow-400 bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
                {currentFight.division}
              </span>
            </div>
            <div
              className={`text-5xl font-black uppercase ${
                phase === "rest" ? "text-blue-400" : "text-red-500"
              } animate-pulse`}
            >
              {phase === "round" ? `ROUND ${currentFight.round || 1}` : "REST"}
            </div>
            <div className="text-6xl font-extrabold w-1/3 text-right">{ring}</div>
          </div>

          {/* ---------------- SCOREBOARD ---------------- */}
          <div className="w-full max-w-6xl border border-gray-700 mb-8">
            {/* HEADER */}
            <div
              className="grid bg-gray-800 text-2xl font-bold"
              style={{
                gridTemplateColumns: `260px repeat(${rounds.length}, 1fr) 140px`,
              }}
            >
              <div className="p-4 text-center">ROUND</div>
              {rounds.map((_, i) => (
                <div key={i} className="p-4 text-center">{i + 1}</div>
              ))}
              <div className="p-4 text-center">TOTAL</div>
            </div>

            {/* RED */}
            <div
              className="grid"
              style={{
                gridTemplateColumns: `260px repeat(${rounds.length}, 1fr) 140px`,
              }}
            >
              <div className="bg-red-800 p-4 text-center font-bold">{currentFight.red}</div>
              {rounds.map((r, i) => (
                <div key={i}>{renderRoundCell(r, "red")}</div>
              ))}
              <div className="bg-red-900 flex items-center justify-center text-5xl font-extrabold">
                {totalRed}
              </div>
            </div>

            {/* BLUE */}
            <div
              className="grid"
              style={{
                gridTemplateColumns: `260px repeat(${rounds.length}, 1fr) 140px`,
              }}
            >
              <div className="bg-blue-800 p-4 text-center font-bold">{currentFight.blue}</div>
              {rounds.map((r, i) => (
                <div key={i}>{renderRoundCell(r, "blue")}</div>
              ))}
              <div className="bg-blue-900 flex items-center justify-center text-5xl font-extrabold">
                {totalBlue}
              </div>
            </div>
          </div>

         {/* UPCOMING */}
<div className="w-full max-w-4xl">
  <div className="grid grid-cols-[1fr_160px_1fr] mb-2 text-2xl font-bold">
    <div className="text-red-500">RED CORNER</div>
    <div className="text-center opacity-80">Division</div>
    <div className="text-blue-500 text-right">BLUE CORNER</div>
  </div>
  <div className="max-h-80 overflow-y-auto">
    {nextFights
      .slice(0, 6)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)) // sort by admin position
      .map((f) => (
        <div
  key={f.id}
  className={`grid grid-cols-[40px_1fr_160px_1fr] p-2 mb-1 ${
    f.id === currentFight?.id
      ? "bg-yellow-900 animate-pulse" // currently live fight
      : f.id === nextFights[0]?.id
      ? "bg-yellow-800 text-black animate-pulse" // next fight pulsing
      : "bg-gray-800 hover:bg-gray-700"
  }`}
>
          {/* Display number based on position */}
          <div className="text-center font-bold">{f.position ?? "–"}</div>
          <div className="font-bold">{f.red}</div>
          <div className="text-center font-bold text-yellow-400">{f.division}</div>
          <div className="text-right font-bold">{f.blue}</div>
        </div>
      ))}
  </div>
</div>
        </>
      )}
    </div>
  );
}
